import React from 'react';

import { Badge, Box, ColorSwatch, Text, Tooltip, UnstyledButton } from '@mantine/core';
import { IconAppWindow, IconCode, IconMailForward, IconPhoneIncoming } from '@tabler/icons-react';
import i18n from 'i18n';
import { DiscordIcon } from 'icons/DiscordIcon';
import { EmbeddedIcon } from 'icons/EmbeddedIcon';
import { FacebookIcon } from 'icons/FacebookIcon';
import { InstagramIcon } from 'icons/InstagramIcon';
import { LinkIcon } from 'icons/LinkIcon';
import { WhatsAppIcon } from 'icons/WhatsAppIcon';
import { ZapierIcon } from 'icons/ZapierIcon';
import { ConversationFilter } from 'shared/constants/conversations';
import {
  Conversation,
  ConversationSourceType,
  HandoffStatus,
  HandoffType,
  TakeoverStatus
} from 'shared/interfaces/Conversation';
import { SmartAssistantType } from 'shared/interfaces/SmartAssistant';

const sourceTypeToIconMap = {
  [ConversationSourceType.DISCORD]: <DiscordIcon w={16} h={16} />,
  [ConversationSourceType.WEBSITE]: <EmbeddedIcon w={14} h={16} />,
  [ConversationSourceType.LINK]: <LinkIcon w={16} h={16} />,
  [ConversationSourceType.ZAPIER]: <ZapierIcon w={16} h={16} />,
  [ConversationSourceType.APP]: <IconAppWindow style={{ width: 16, height: 16 }} />,
  [ConversationSourceType.API]: <IconCode style={{ width: 16, height: 16 }} />,
  [ConversationSourceType.WHATSAPP]: <WhatsAppIcon w={16} h={16} />,
  [ConversationSourceType.FACEBOOK]: <FacebookIcon w={18} h={18} />,
  [ConversationSourceType.INSTAGRAM]: <InstagramIcon w={18} h={18} />,
  [ConversationSourceType.CALL]: <IconPhoneIncoming style={{ width: 16, height: 16 }} />
};

const handoffStatusToIconMap = {
  [HandoffStatus.RESOLVED]: <ColorSwatch w={16} h={16} color="green" />,
  [HandoffStatus.UNRESOLVED]: <ColorSwatch w={16} h={16} color="orange" />,
  [HandoffStatus.ALL]: <ColorSwatch w={16} h={16} color="white" />
};

const takeoverStatusToIconMap = {
  [TakeoverStatus.ON]: <ColorSwatch w={16} h={16} color="orange" />,
  [TakeoverStatus.OFF]: <ColorSwatch w={16} h={16} color="green" />,
  [TakeoverStatus.ALL]: <ColorSwatch w={16} h={16} color="white" />
};

export const CONVERSATION_SOURCE_TYPES_BY_SMART_ASSISTANT_TYPE: Record<SmartAssistantType, ConversationSourceType[]> = {
  [SmartAssistantType.BASIC]: [
    ConversationSourceType.APP,
    ConversationSourceType.API,
    ConversationSourceType.ZAPIER,
    ConversationSourceType.LINK
  ],
  [SmartAssistantType.WEBSITE]: [
    ConversationSourceType.WEBSITE,
    ConversationSourceType.APP,
    ConversationSourceType.API,
    ConversationSourceType.ZAPIER,
    ConversationSourceType.LINK
  ],
  [SmartAssistantType.SHOPIFY]: [ConversationSourceType.WEBSITE, ConversationSourceType.APP],
  [SmartAssistantType.INSTAGRAM]: [ConversationSourceType.INSTAGRAM, ConversationSourceType.APP],
  [SmartAssistantType.FACEBOOK]: [ConversationSourceType.FACEBOOK, ConversationSourceType.APP],
  [SmartAssistantType.DISCORD]: [ConversationSourceType.DISCORD, ConversationSourceType.APP],
  [SmartAssistantType.PHONE]: [ConversationSourceType.CALL],
  [SmartAssistantType.WHATSAPP]: [ConversationSourceType.WHATSAPP, ConversationSourceType.APP],
  // The following smart assistant types are not implemented yet
  [SmartAssistantType.SMS]: [],
  [SmartAssistantType.BIGCOMMERCE]: [],
  [SmartAssistantType.WIX]: [],
  [SmartAssistantType.WOOCOMMERCE]: [],
  [SmartAssistantType.SQUARESPACE]: []
};

export const SMART_ASSISTANT_TYPES_CONVERSATION_FILTERS: Record<SmartAssistantType, ConversationFilter[]> = {
  [SmartAssistantType.BASIC]: ['dateRange', 'sourceTypes'],
  [SmartAssistantType.WEBSITE]: ['dateRange', 'sourceTypes', 'handoffStatus'],
  [SmartAssistantType.SHOPIFY]: ['dateRange', 'sourceTypes', 'handoffStatus'],
  [SmartAssistantType.INSTAGRAM]: ['dateRange', 'sourceTypes', 'takeoverStatus'],
  [SmartAssistantType.FACEBOOK]: ['dateRange', 'sourceTypes', 'takeoverStatus'],
  [SmartAssistantType.DISCORD]: ['dateRange', 'sourceTypes'],
  [SmartAssistantType.PHONE]: ['dateRange'],
  [SmartAssistantType.WHATSAPP]: ['dateRange', 'sourceTypes', 'takeoverStatus'],
  // The following smart assistant types are not implemented yet
  [SmartAssistantType.SMS]: [],
  [SmartAssistantType.BIGCOMMERCE]: [],
  [SmartAssistantType.WIX]: [],
  [SmartAssistantType.WOOCOMMERCE]: [],
  [SmartAssistantType.SQUARESPACE]: []
};

export const CAN_TAKEOVER_SOURCE_TYPES = [
  ConversationSourceType.WHATSAPP,
  ConversationSourceType.FACEBOOK,
  ConversationSourceType.INSTAGRAM
];

export const getSourceTypeSelectDataBySmartAssistantType = (smartAssistantType: SmartAssistantType) => {
  return CONVERSATION_SOURCE_TYPES_BY_SMART_ASSISTANT_TYPE[smartAssistantType].map((sourceType) => ({
    label: i18n.t(`SMART_ASSISTANT.CONVERSATIONS_SECTION.SOURCES.${sourceType.toUpperCase()}.LABEL`),
    value: sourceType,
    iconComponent: sourceTypeToIconMap[sourceType],
    info: i18n.t(`SMART_ASSISTANT.CONVERSATIONS_SECTION.SOURCES.${sourceType.toUpperCase()}.INFO`)
  }));
};

export const HANDOFF_STATUS_SELECT_DATA = Object.values(HandoffStatus).map((status) => ({
  label: i18n.t(`SMART_ASSISTANT.CONVERSATIONS_SECTION.HANDOFF.STATUS.${status.toUpperCase()}.LABEL`),
  value: status,
  icon: handoffStatusToIconMap[status],
  info: i18n.t(`SMART_ASSISTANT.CONVERSATIONS_SECTION.HANDOFF.STATUS.${status.toUpperCase()}.INFO`)
}));

export const TAKEOVER_STATUS_SELECT_DATA = Object.values(TakeoverStatus).map((status) => ({
  label: i18n.t(`SMART_ASSISTANT.CONVERSATIONS_SECTION.TAKEOVER.STATUS.${status.toUpperCase()}.LABEL`),
  value: status,
  icon: takeoverStatusToIconMap[status],
  info: i18n.t(`SMART_ASSISTANT.CONVERSATIONS_SECTION.TAKEOVER.STATUS.${status.toUpperCase()}.INFO`)
}));

export const ConversationHandoffStatusBadge = ({ status }: { status: HandoffStatus }) => {
  switch (status) {
    case HandoffStatus.RESOLVED:
      return (
        <Badge color="green" size="xs" tt="capitalize">
          {i18n.t('SMART_ASSISTANT.CONVERSATIONS_SECTION.HANDOFF.STATUS.RESOLVED.LABEL')}
        </Badge>
      );
    case HandoffStatus.UNRESOLVED:
      return (
        <Badge color="orange" size="xs" tt="capitalize">
          {i18n.t('SMART_ASSISTANT.CONVERSATIONS_SECTION.HANDOFF.STATUS.UNRESOLVED.LABEL')}
        </Badge>
      );
    default:
      return null;
  }
};

export const ConversationHandoffTypeBadge = ({ type }: { type: HandoffType }) => {
  switch (type) {
    case HandoffType.EMAIL:
      return (
        <Badge
          size="md"
          color="blue"
          tt="capitalize"
          leftSection={<IconMailForward style={{ width: 15, height: 15 }} />}
          fullWidth>
          {i18n.t('SMART_ASSISTANT.CONVERSATIONS_SECTION.HANDOFF.TYPE.EMAIL')}
        </Badge>
      );
    default:
      return null;
  }
};

export const ConversationSourceComponent = ({ conversation }: { conversation: Conversation }) => {
  const icon = sourceTypeToIconMap[conversation.source.type];
  return (
    <Tooltip
      multiline
      position="right"
      withArrow
      offset={10}
      w={250}
      withinPortal
      label={
        <Box>
          <Text>
            {i18n.t(`SMART_ASSISTANT.CONVERSATIONS_SECTION.SOURCES.${conversation.source.type.toUpperCase()}.INFO`)}
          </Text>
          {conversation.source.type === ConversationSourceType.DISCORD && (
            <Text mt={15}>
              {i18n.t(
                `SMART_ASSISTANT.CONVERSATIONS_SECTION.SOURCES.${conversation.source.type.toUpperCase()}.CHANNEL`,
                {
                  name: conversation.title
                }
              )}
            </Text>
          )}
        </Box>
      }>
      <UnstyledButton>{icon}</UnstyledButton>
    </Tooltip>
  );
};
